import { Component, ElementRef, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ComponentSettingsReturn } from 'node_js/models/business/component-settings-return';
import { ComponentComplete } from 'node_js/models/business/lab/component-complete';
import { FileUploadComplete } from 'node_js/models/business/lab/file-upload-complete';
import { GadgetComplete } from 'node_js/models/business/lab/gadget-complete';
import { LabComplete } from 'node_js/models/business/lab/lab-complete';
import { LanguageEnum } from 'node_js/models/enums/language';
import { RoleEnum } from 'node_js/models/enums/role';
import { SnackBarColor } from 'node_js/models/enums/snackbar-color';
import { LabService } from 'src/app/services/lab.service';
import { LanguageService } from 'src/app/services/language.service';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { presentLoading, presentSnackBar } from 'src/global';
import { Platform } from '@ionic/angular';
import { AndroidBackPriority } from 'node_js/models/enums/android-back-priority';
import Swiper from 'swiper';
import { TutorialService } from 'src/app/services/tutorial.service';

@Component({
  selector: 'create-gadget-component',
  templateUrl: './create-gadget-component.component.html',
  styleUrls: ['./create-gadget-component.component.scss'],
})
export class CreateGadgetComponentComponent implements OnInit, OnDestroy, AfterViewInit {

  componentName:string = "";
  componentNameLength = 255;
  private readonly imagePickerSlideDurationMs = 350;

  selectedGadgetComplete:GadgetComplete;

  @ViewChild('mainSwiper') mainSwiperRef!: ElementRef;
    
  iconFileUploadComplete:FileUploadComplete;
  selectedCuratedImage: string | null = '../../assets/icon/default_component.jpg';
  
  // Back button subscription
  private backButtonSub: any;

  constructor(private languageService:LanguageService,public matDialogRef: MatDialogRef<CreateGadgetComponentComponent>,
    private snackbar:MatSnackBar, private userAuthService:UserAuthService, private labService:LabService,
    private matDialog:MatDialog, private matSnackBar:MatSnackBar, private platform:Platform,
    private tutorialService: TutorialService) {
  }

  ngOnInit() {
    this.selectedGadgetComplete = this.labService.currentSelectedGadget;
    
    // Set up back button handler
    this.handleBackButton();
  }

  ngAfterViewInit() {
    // Set initial selected image
    this.selectedCuratedImage = '../../assets/icon/default_component.jpg';
  }

  getDisplayImage(): string {
    // Prioritize uploaded image if available
    if (this.iconFileUploadComplete) {
      return this.iconFileUploadComplete.srcUrl;
    }
    return this.selectedCuratedImage || '../../assets/icon/default_component.jpg';
  }

  onComponentNameChange(componentName: string) {
    this.tutorialService.noteComponentNameEntered(componentName);
  }

  onImageClick() {
    this.tutorialService.noteCreateComponentIconPickerOpened();

    // Navigate to second slide when clicking on image
    const swiper: Swiper = this.mainSwiperRef.nativeElement.swiper;
    if (swiper) {
      this.tutorialService.pauseOverlayFor(this.imagePickerSlideDurationMs);
      swiper.slideNext(this.imagePickerSlideDurationMs);
    }
  }

  returnToFirstSlide() {
    const swiper: Swiper = this.mainSwiperRef.nativeElement.swiper;
    if (swiper) {
      this.tutorialService.pauseOverlayFor(this.imagePickerSlideDurationMs);
      swiper.slideTo(0, this.imagePickerSlideDurationMs);
    }
  }

  onCuratedImageSelected(event: { url: string }) {
    this.selectedCuratedImage = event.url;
    this.iconFileUploadComplete = null;
    this.tutorialService.noteCreateComponentCuratedIconSelected();
  }

  onImageUploaded(fileUploadComplete: FileUploadComplete) {
    this.iconFileUploadComplete = fileUploadComplete;
  }

  createComponent(){

    if (this.componentName == ""){
      presentSnackBar("You need to enter in a component name!",SnackBarColor.DANGER,this.snackbar);
    }
    else if (this.componentName.length > this.componentNameLength){
      presentSnackBar(this.languageService.instant("A component name cannot be longer than {{param1}} characters!", { param1: this.componentNameLength }),SnackBarColor.DANGER,this.snackbar);
    }
    else{
      let loadingRef = presentLoading(this.matDialog);
      const curatedIconToUse = !this.iconFileUploadComplete && this.selectedCuratedImage ? this.selectedCuratedImage : undefined;
      this.labService.createGadgetComponent(this.userAuthService,this.componentName,this.languageService.activeLanguage.id,this.selectedGadgetComplete.gadget.id,this.iconFileUploadComplete, curatedIconToUse).then((componentSettingsReturn:ComponentSettingsReturn)=>{
        loadingRef.close();
        this.labService.updateComponentSettingsLocally(componentSettingsReturn,this.userAuthService,false);
        console.log("user complete",this.userAuthService.currentUserComplete);

        if (this.tutorialService.isCreateComponentTutorialActive) {
          this.tutorialService.completeCreateComponentTutorial();
          presentSnackBar("Component tutorial complete. Your new component is ready.", SnackBarColor.SUCCESS, this.snackbar);
        }

        this.matDialogRef.close(componentSettingsReturn.componentComplete);
     }).catch(err=>{
      loadingRef.close();
      console.log(err);
      presentSnackBar(this.languageService.getTranslation("generic.unexpected-error",false),SnackBarColor.DANGER,this.snackbar);

     })
    }
   
  }

  /**
   * Handle back button press when component is loaded as a dialog
   */
  handleBackButton() {
    console.log('[CreateGadgetComponent] handleBackButton called');
    
    if (this.platform && this.platform.backButton) {
      console.log('[CreateGadgetComponent] Setting up back button handler with priority:', AndroidBackPriority.DIALOG);
      this.backButtonSub = this.platform.backButton.subscribeWithPriority(AndroidBackPriority.DIALOG, () => {
        console.log('[CreateGadgetComponent] 🎯 BACK BUTTON PRESSED! Closing dialog...');
        this.closeDialog();
      });
      console.log('[CreateGadgetComponent] ✅ Back button handler set up successfully');
    } else {
      console.log('[CreateGadgetComponent] Platform or backButton not available');
    }
  }

  /**
   * Clean up back button subscription when component is destroyed
   */
  ngOnDestroy() {
    console.log('[CreateGadgetComponent] ngOnDestroy called, cleaning up back button subscription');
    this.cleanupBackButtonSubscription();
  }

  /**
   * Clean up back button subscription manually
   */
  private cleanupBackButtonSubscription() {
    if (this.backButtonSub) {
      try {
        this.backButtonSub.unsubscribe();
        console.log('[CreateGadgetComponent] ✅ Back button subscription cleaned up');
      } catch (err) {
        console.warn('[CreateGadgetComponent] Error cleaning up back button subscription:', err);
      }
      this.backButtonSub = null;
    }
  }

  closeDialog() {
    this.tutorialService.noteCreateComponentDialogClosed(false);
    this.matDialogRef.close();
  }

  /**
   * Public method to cleanup back button subscription before dialog closes
   */
  public cleanupBeforeClose() {
    console.log('[CreateGadgetComponent] cleanupBeforeClose called');
    this.cleanupBackButtonSubscription();
  }
}
